import * as React from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { toast } from "sonner";

import { PlatformsStep } from "@/features/onboarding/components/PlatformsStep";
import { api } from "@/trpc/react";

export const Route = createFileRoute(
  "/_authenticated/(wonda)/_wonda/onboarding/platforms",
)({
  beforeLoad: async ({ context }) => {
    const answers =
      await context.trpcQueryUtils.account.getWelcomeAnswers.ensureData();
    if (!answers?.tools) {
      throw redirect({ to: "/onboarding/tools" });
    }
  },
  component: PlatformsRoute,
});

function PlatformsRoute() {
  const navigate = useNavigate();
  const posthog = usePostHog();

  const { data: welcomeAnswers, isLoading } =
    api.account.getWelcomeAnswers.useQuery();
  const saveWelcomeAnswer = api.account.saveWelcomeAnswer.useMutation();

  const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>(
    () => welcomeAnswers?.platforms ?? [],
  );

  // Hydrate from server answers once they arrive.
  const hasHydratedRef = React.useRef(false);
  React.useEffect(() => {
    if (hasHydratedRef.current) return;
    if (!welcomeAnswers?.platforms) return;
    hasHydratedRef.current = true;
    setSelectedPlatforms(welcomeAnswers.platforms);
  }, [welcomeAnswers]);

  React.useEffect(() => {
    const STORAGE_KEY = "welcome-question-viewed:platforms";
    if (typeof window === "undefined") return;
    if (isLoading) return;
    if (!welcomeAnswers?.tools) return;
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;
    window.sessionStorage.setItem(STORAGE_KEY, "1");
    posthog.capture("welcome question viewed", { question: "platforms" });
  }, [posthog, isLoading, welcomeAnswers]);

  return (
    <PlatformsStep
      selectedPlatforms={selectedPlatforms}
      setSelectedPlatforms={setSelectedPlatforms}
      posthog={posthog}
      isPending={saveWelcomeAnswer.isPending}
      onContinue={(platforms) => {
        // The guide cohort is derived server-side from this answer (see
        // getWelcomeAnswers): LinkedIn or Sales Navigator here funnels the
        // user to the LinkedIn guide variant regardless of signup path.
        saveWelcomeAnswer.mutate(
          { platforms },
          {
            onSuccess: () => {
              void navigate({ to: "/onboarding/paywall" });
            },
            onError: (error) => {
              toast.error(error.message);
            },
          },
        );
      }}
      onBack={() => {
        void navigate({ to: "/onboarding/tools" });
      }}
    />
  );
}
